import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { AITeamOrbit } from '@/components/landing/AITeamOrbit'
import { DirectionalList } from '@/components/landing/DirectionalList'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { SubscribeBand } from '@/components/landing/SubscribeBand'
import { AgentSteps } from '@/components/landing/home/AgentSteps'
import { IndustryScenarios } from '@/components/landing/home/IndustryScenarios'
import {
  ScrollReveal,
  TextReveal,
  HeroReveal,
  Pressable,
  BandReveal,
  InteractiveDots,
} from '@/components/motion'

const OFFICE_APPS: Array<{ app: OfficeApp; name: string }> = [
  { app: 'word', name: 'Word' },
  { app: 'excel', name: 'Excel' },
  { app: 'powerpoint', name: 'PowerPoint' },
  { app: 'outlook', name: 'Outlook' },
]

/**
 * Landing page, six sections, each answering one question in turn, with
 * backgrounds alternating light / colour / grey / white / grey / colour:
 * 1. Hero: what is this? (calls to action live here and in the band)
 * 2. Promise band: why should I care?
 * 3. Agents and how they make a document: how does it work?
 * 4. Platform: what does it cover, and where does it run?
 * 5. Real work, by industry: does it fit my work?
 * 6. Subscribe.
 * Customer quotes (`components/landing/home/TestimonialsSection`) slot in
 * before the subscribe band once there are real ones.
 */
export default function Home() {
  return (
    <>
      <PageMeta
        title="Templatean | On-time, on-brand documents inside Microsoft Office"
        description="AI agents that draft, polish and check documents from your own templates and brand rules, inside Word, Excel, PowerPoint and Outlook."
      />

      <LandingLayout>
        {/* 1. Hero: fills the viewport and runs under the transparent 72px
            nav. The headline and its sentence sit at the screen's vertical
            centre; the two actions sit at the bottom. Office tiles drift
            behind, between nav and actions. */}
        <section className="relative overflow-hidden landing-bg-gradient-hero -mt-[72px] h-[max(100svh,38rem)] grid grid-rows-[1fr_auto_1fr]">
          <div aria-hidden className="hidden md:block absolute inset-x-0 top-[72px] bottom-[13rem] pointer-events-none z-0">
            {([
              { app: 'word', top: '8%', left: '6%', dx: '54vw', dy: '6vh', delay: '0s', amp: '7vh', wave: '9s', waveDelay: '-2s', float: 'hero-float-a' },
              { app: 'excel', top: '40%', left: '3%', dx: '58vw', dy: '-8vh', delay: '-20s', amp: '5vh', wave: '13s', waveDelay: '-7s', float: 'hero-float-b' },
              { app: 'powerpoint', top: '30%', left: '13%', dx: '50vw', dy: '-2vh', delay: '-40s', amp: '6vh', wave: '7s', waveDelay: '-4s', float: 'hero-float-c' },
              { app: 'outlook', top: '4%', left: '20%', dx: '48vw', dy: '10vh', delay: '-60s', amp: '8vh', wave: '11s', waveDelay: '-9s', float: 'hero-float-a' },
            ] as const).map((tile) => (
              <div
                key={tile.app}
                className="absolute hero-office-drift"
                style={{
                  top: tile.top,
                  left: tile.left,
                  ['--dx' as string]: tile.dx,
                  ['--dy' as string]: tile.dy,
                  ['--drift-delay' as string]: tile.delay,
                }}
              >
                <div
                  className="hero-office-wave"
                  style={{
                    ['--wave-amp' as string]: tile.amp,
                    ['--wave-dur' as string]: tile.wave,
                    ['--wave-delay' as string]: tile.waveDelay,
                  }}
                >
                  <div className={`flex h-[clamp(5rem,18vh,9rem)] w-[clamp(5rem,18vh,9rem)] items-center justify-center bg-white/30 dark:bg-white/[0.05] backdrop-blur-md border border-white/50 dark:border-white/10 ${tile.float}`}>
                    <OfficeAppIcon app={tile.app} className="h-[56%] w-[56%] opacity-55" />
                  </div>
                </div>
              </div>
            ))}
          </div>


          {/* Row 1: equal to row 3, so the message sits at the screen's
              vertical centre (the hero starts under the 72px nav). */}
          <div aria-hidden className="min-h-[72px]" />

          {/* Row 2: the key message, centred */}
          <div className="landing-container relative z-10 py-8">
              <HeroReveal delay={0.05} className="relative z-10">
                <h1 aria-label="Documents on time" className="text-center font-[family-name:var(--font-landing-display)] uppercase text-[clamp(2.3375rem,min(8.5vw,15svh),8.925rem)] leading-[1.2] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                  <span className="block">
                    <span className="text-[var(--color-landing-accent)]">Do</span>cuments
                  </span>
                  <span className="block mt-[0.1em]">
                    <span className="text-[var(--color-landing-accent)]">On time</span>
                  </span>
                </h1>
              </HeroReveal>
          </div>

          {/* Row 3: the sentence and the two actions, left-aligned at the
              bottom of the hero. When the screen is too short for exact
              centring, row 1 gives way first, so the actions stay in view. */}
          <div className="landing-container relative z-10 flex flex-col justify-end pb-8 pt-6 lg:pb-10">
            <HeroReveal delay={0.25}>
              <p className="max-w-3xl text-[15px] leading-[1.5] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] lg:text-base">
                Templatean turns your templates, brand assets and business logic
                into AI agents that draft, polish and ship professional
                documents—inside Word, Excel, PowerPoint, and Outlook.
              </p>
            </HeroReveal>
            <HeroReveal delay={0.4}>
              {/* Stacked at equal width on phones, side by side from sm up */}
              <div className="mt-6 flex w-[min(20rem,100%)] flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <Pressable className="block">
                  <Link to="/register" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep w-full justify-center sm:w-auto">
                    Start free trial
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Pressable>
                <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep w-full justify-center sm:w-auto">
                  Book a demo
                </Link>
              </div>
            </HeroReveal>
          </div>
        </section>

        {/* 2. Promise band: the positioning line and the problem it answers */}
        <BandReveal>
          <section className="landing-section relative overflow-hidden bg-[var(--color-landing-accent)]">
            <InteractiveDots interactive />
            <div className="landing-container relative z-10 text-center">
              <TextReveal
                as="h2"
                className="landing-text-display-xl text-white"
                lines={[
                  'Your templates.',
                  'Your brand rules.',
                  'AI that follows both.',
                ]}
              />
              <ScrollReveal direction="none" delay={0.15}>
                <p className="landing-text-body-lg mx-auto mt-10 max-w-[56ch] text-pretty text-white/85 md:text-xl">
                  Your expertise lives in people&rsquo;s heads, not in your systems. Generic AI doesn&rsquo;t know
                  your terminology, your processes or your rules. Templatean&rsquo;s agents work from yours.
                </p>
              </ScrollReveal>
              <ScrollReveal direction="none" delay={0.25}>
                <Pressable className="mt-10 inline-block">
                  <Link
                    to="/contact"
                    className="landing-cta landing-cta--solid landing-cta--lg landing-cta--on-color landing-btn-sweep"
                  >
                    Book a demo
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Pressable>
              </ScrollReveal>
            </div>
          </section>
        </BandReveal>

        {/* 3. The agents, then how they make a document */}
        <AITeamOrbit className="bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
          <AgentSteps />
        </AITeamOrbit>

        {/* 4. Platform: the four pillars, then where it runs */}
        <section className="landing-section">
          <h2 className="sr-only">Platform overview</h2>
          <DirectionalList
            items={[
              {
                index: '01',
                title: 'Templates',
                description: 'Living templates with governed fields, versioning and brand controls baked in.',
                href: '/platform/templates',
                color: 'blue',
                visuals: [{ kind: 'doc' }, { kind: 'fields' }, { kind: 'slide' }],
              },
              {
                index: '02',
                title: 'AI agents',
                description: 'Agents that know your business logic, chained into workflows that ship documents end to end.',
                href: '/platform/ai-generation',
                color: 'pink',
                visuals: [
                  { kind: 'shot', pos: '45% 26%', label: 'Agent chain' },
                  { kind: 'shot', pos: '43% 70%', label: 'Risk check' },
                  { kind: 'shot', pos: '97% 30%', label: 'Agent config' },
                ],
              },
              {
                index: '03',
                title: 'Brand assets',
                description: 'One governed library for logos, imagery and charts, delivered inside Office.',
                href: '/platform/dam',
                color: 'purple',
                visuals: [{ kind: 'library' }, { kind: 'swatches' }, { kind: 'type' }],
              },
              {
                index: '04',
                title: 'Analytics',
                description: 'Usage analytics and audit trails that connect every document to a real outcome.',
                href: '/platform/analytics',
                color: 'blue',
                visuals: [{ kind: 'chart' }, { kind: 'trend' }, { kind: 'audit' }],
              },
            ]}
          />

          <div className="landing-container mt-16 md:mt-24">
            <ScrollReveal direction="none">
              <div className="grid grid-cols-1 items-center gap-8 border-t border-[var(--color-landing-gray-200)] pt-10 dark:border-[var(--color-landing-gray-800)] lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-14">
                <ul aria-label="Works inside" className="flex flex-wrap gap-x-10 gap-y-5">
                  {OFFICE_APPS.map(({ app, name }) => (
                    <li key={app} className="flex items-center gap-3">
                      <OfficeAppIcon app={app} className="h-10 w-10" />
                      <span className="font-[family-name:var(--font-landing-subdisplay)] text-2xl font-semibold tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                        {name}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="landing-text-body max-w-[52ch] text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                  Works inside the Office apps your team already uses, and fills documents from HubSpot,
                  SharePoint, Excel Online, SQL databases and REST APIs.
                </p>
                <Link
                  to="/platform/integrations"
                  className="group inline-flex items-center gap-2 font-semibold text-[var(--color-landing-accent)] dark:text-[#8a84ff]"
                >
                  See all integrations
                  <ArrowRight aria-hidden className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 5. Real work, by industry */}
        <IndustryScenarios />

        {/* 6. Subscribe */}
        <SubscribeBand />

      </LandingLayout>
    </>
  )
}
